<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SegmentedControl from 'dashboard/components-next/segmented-control/SegmentedControl.vue';

const props = defineProps({
  agent: { type: Object, required: true },
});

// Kept in sync with User::PASSWORD_RESET_LINK_EXPIRY_MINUTES - the backend falls back to
// Devise's global window for anything it doesn't recognise.
const EXPIRY_OPTIONS = [
  { value: 15, key: 'FIFTEEN_MINUTES' },
  { value: 60, key: 'ONE_HOUR' },
  { value: 360, key: 'SIX_HOURS' },
];

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const busyAction = ref('');
const passwordResetExpiry = ref(EXPIRY_OPTIONS[0].value);

const expiryOptions = computed(() =>
  EXPIRY_OPTIONS.map(({ value, key }) => ({
    value,
    label: t(`AGENT_MGMT.ACCESS_LINKS.DIALOG.PASSWORD_RESET.EXPIRY.${key}`),
  }))
);

const generateLink = linkType =>
  linkType === 'DIRECT_ACCESS'
    ? store.dispatch('agents/generateDirectAccessLink', props.agent.id)
    : store.dispatch('agents/generatePasswordResetLink', {
        agentId: props.agent.id,
        expiresInMinutes: passwordResetExpiry.value,
      });

const handleLinkAction = async (linkType, mode) => {
  busyAction.value = `${linkType}:${mode}`;
  try {
    const url = await generateLink(linkType);
    if (mode === 'open') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      await copyTextToClipboard(url);
      useAlert(t(`AGENT_MGMT.ACCESS_LINKS.${linkType}_COPIED`));
    }
  } catch (error) {
    useAlert(t('AGENT_MGMT.ACCESS_LINKS.API.ERROR_MESSAGE'));
  } finally {
    busyAction.value = '';
  }
};

defineExpose({ open: () => dialogRef.value?.open() });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="$t('AGENT_MGMT.ACCESS_LINKS.DIALOG.TITLE')"
    :description="
      $t('AGENT_MGMT.ACCESS_LINKS.DIALOG.DESCRIPTION', { name: agent.name })
    "
    :show-confirm-button="false"
    :cancel-button-label="$t('DIALOG.BUTTONS.CLOSE')"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_MGMT.ACCESS_LINKS.DIALOG.DIRECT_ACCESS.TITLE') }}
          </span>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('AGENT_MGMT.ACCESS_LINKS.DIALOG.DIRECT_ACCESS.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            faded
            slate
            sm
            type="button"
            icon="i-lucide-external-link"
            :label="$t('AGENT_MGMT.ACCESS_LINKS.DIALOG.OPEN')"
            :is-loading="busyAction === 'DIRECT_ACCESS:open'"
            @click="handleLinkAction('DIRECT_ACCESS', 'open')"
          />
          <Button
            faded
            slate
            sm
            type="button"
            icon="i-lucide-copy"
            :label="$t('AGENT_MGMT.ACCESS_LINKS.DIALOG.COPY')"
            :is-loading="busyAction === 'DIRECT_ACCESS:copy'"
            @click="handleLinkAction('DIRECT_ACCESS', 'copy')"
          />
        </div>
      </div>

      <div class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak">
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_MGMT.ACCESS_LINKS.DIALOG.PASSWORD_RESET.TITLE') }}
          </span>
          <p class="mb-0 text-sm text-n-slate-11">
            {{
              $t('AGENT_MGMT.ACCESS_LINKS.DIALOG.PASSWORD_RESET.DESCRIPTION')
            }}
          </p>
        </div>
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-n-slate-11">
            {{
              $t('AGENT_MGMT.ACCESS_LINKS.DIALOG.PASSWORD_RESET.EXPIRY_LABEL')
            }}
          </span>
          <SegmentedControl
            v-model="passwordResetExpiry"
            :options="expiryOptions"
          />
        </div>
        <div class="flex items-center gap-2">
          <Button
            faded
            slate
            sm
            type="button"
            icon="i-lucide-external-link"
            :label="$t('AGENT_MGMT.ACCESS_LINKS.DIALOG.OPEN')"
            :is-loading="busyAction === 'PASSWORD_RESET:open'"
            @click="handleLinkAction('PASSWORD_RESET', 'open')"
          />
          <Button
            faded
            slate
            sm
            type="button"
            icon="i-lucide-copy"
            :label="$t('AGENT_MGMT.ACCESS_LINKS.DIALOG.COPY')"
            :is-loading="busyAction === 'PASSWORD_RESET:copy'"
            @click="handleLinkAction('PASSWORD_RESET', 'copy')"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
